function daysBetweenDates(date1, date2) {
  // Convert dates to milliseconds
  const date1_ms = new Date(date1).getTime();
  const date2_ms = new Date(date2).getTime();

  // Calculate the difference in milliseconds
  const difference_ms = Math.abs(date2_ms - date1_ms);

  // Convert back to days and round up
  const days = Math.ceil(difference_ms / (1000 * 60 * 60 * 24));

  return days;
}

// Function to calculate price before tax and the tax amount
// Takes price in cents, returns in euros
function calculatePriceAndTax(priceWithTaxCents, taxRate = 24) {
  // Convert the tax rate from a percentage to a decimal
  const taxRateDecimal = taxRate / 100;

  // Calculate the price before tax in cents
  const priceBeforeTaxCents = priceWithTaxCents / (1 + taxRateDecimal);

  // Convert price from cents to euros
  const priceBeforeTaxEuros = priceBeforeTaxCents / 100;

  // Calculate the tax amount in euros
  const taxAmountEuros = priceWithTaxCents / 100 - priceBeforeTaxEuros;

  // console.log("calculatePriceAndTax", { taxRate });

  return {
    priceBeforeTax: parseFloat(priceBeforeTaxEuros.toFixed(2)), // rounded to 2 decimal places
    taxAmount: parseFloat(taxAmountEuros.toFixed(2)), // rounded to 2 decimal places
  };
}

function mapVehicleOffer(offer) {
  return {
    item_id: offer.vehicle.id,
    item_name: offer.vehicle.name,
    item_category: offer.vehicle.category.name,
    item_category2: offer.vehicle.category.typeId,
  };
}

function mapServiceOffer(offer) {
  const taxDetails = calculatePriceAndTax(offer.offerPrice, offer.tax);
  const item = {
    item_id: offer.id,
    item_name: offer.description,
    item_category: offer.category,
    price: taxDetails.priceBeforeTax,
    taxAmount: taxDetails.taxAmount,
    quantity: offer.quantity || 1,
  };
  return item;
}

function getSharedParams(cart, services) {
  const vehicleItems = cart.map((offer) => {
    const tax = offer.tax || offer.vehicle?.tax;
    const taxDetails = calculatePriceAndTax(offer.offerPrice, tax);
    return {
      ...mapVehicleOffer(offer),
      price: taxDetails.priceBeforeTax,
      taxAmount: taxDetails.taxAmount,
      quantity: offer.quantity,
    };
  });
  const serviceItems = services.map((serviceOffer) => {
    return mapServiceOffer(serviceOffer);
  });
  const currency = "EUR";
  const items = [...vehicleItems, ...serviceItems];
  const value = items.reduce(
    (acc, curr) => acc + curr.price * curr.quantity,
    0
  );
  const taxAmount = items.reduce(
    (acc, curr) => acc + curr.taxAmount * curr.quantity,
    0
  );
  const params = {
    currency: currency,
    value: value,
    tax: taxAmount,
    items: items,
  };
  return params;
}

// GA4 add_shipping_info ecommerce event
export function gtmPurchase(reservationInfo, company) {
  if (!enableGTMEvents(company)) return;
  // console.log("gtmPurchase", reservationInfo);
  let taxAmount = 0;
  const vehicleItems = reservationInfo.vehicles.map((v) => {
    const taxDetails = calculatePriceAndTax(v.price, v.tax);
    taxAmount = taxAmount + taxDetails.taxAmount;
    return {
      item_id: v.id,
      item_name: v.name,
      item_category: v.category.name,
      item_category2: v.category.typeId,
      price: taxDetails.priceBeforeTax,
      quantity: 1,
    };
  });
  const serviceItems = reservationInfo.services.map((s) => {
    const taxDetails = calculatePriceAndTax(s.price, s.tax);
    taxAmount = taxAmount + taxDetails.taxAmount;
    return {
      item_id: s.key,
      item_name: s.description,
      item_category: s.category,
      price: taxDetails.priceBeforeTax,
      quantity: s.quantity,
    };
  });
  const items = [...vehicleItems, ...serviceItems];
  const value = items.reduce(
    (acc, curr) => acc + curr.price * curr.quantity,
    0
  );
  const params = {
    transaction_id: `${reservationInfo.id}_${reservationInfo.paymentStatus}`,
    currency: "EUR",
    value: value,
    tax: taxAmount,
    coupon: reservationInfo.voucherData?.code,
    items: items,
  };
  gtagPageView("purchase", params);
}

// GA4 add_shipping_info ecommerce event
export function gtmBeginCheckout(cart, services, company) {
  if (!enableGTMEvents(company)) return;
  // console.log("gtmBeginCheckout", cart);
  const params = getSharedParams(cart, services);
  gtagPageView("beging_checkout", params);
}

// GA4 add_shipping_info ecommerce event
export function gtmAddShippingInfo(cart, services, company) {
  if (!enableGTMEvents(company)) return;
  // console.log("gtmAddShippingInfo", cart);
  const params = getSharedParams(cart, services);
  gtagPageView("add_shipping_info", params);
}

// GA4 add_payment_info ecommerce event
export function gtmAddPaymentInfo(cart, services, company) {
  if (!enableGTMEvents(company)) return;
  // console.log("gtmAddPaymentInfo", cart);
  const params = getSharedParams(cart, services);
  gtagPageView("add_payment_info", params);
}

// GA4 add_to_cart ecommerce event for AdditionalService
export function gtmRemoveServiceFromCart(product, company) {
  if (!enableGTMEvents(company)) return;
  // console.log("gtmRemoveServiceFromCart", product);
  const item = mapServiceOffer(product);
  const params = {
    currency: "EUR",
    value: item.price,
    items: [item],
  };
  gtagPageView("remove_from_cart", params);
}

// GA4 add_to_cart ecommerce event for AdditionalService
export function gtmAddServiceToCart(product, company) {
  if (!enableGTMEvents(company)) return;
  // console.log("gtmAddServiceToCart", product);
  const item = mapServiceOffer(product);
  const params = {
    currency: "EUR",
    value: item.price,
    items: [item],
  };
  gtagPageView("add_to_cart", params);
}

// GA4 add_to_cart ecommerce event
export function gtmAddVehicleToCart(product, company) {
  if (!enableGTMEvents(company)) return;
  // console.log("gtmAddToCart", product);
  const taxDetails = calculatePriceAndTax(product.offerPrice, product.tax);
  const item = {
    item_id: product.vehicle.id,
    item_name: product.vehicle.name,
    item_category: product.vehicle.category.name,
    item_category2: product.vehicle.category.typeId,
    price: taxDetails.priceBeforeTax,
    taxAmount: taxDetails.taxAmount,
  };
  const params = {
    currency: "EUR",
    value: taxDetails.priceBeforeTax,
    items: [item],
  };
  gtagPageView("add_to_cart", params);
}

// GA4 view_item_list ecommerce event
export function gtmViewItemList(
  reservationOffers,
  startTime,
  returnTime,
  company
) {
  if (!enableGTMEvents(company)) return;

  // console.log("gtmViewItemList offers", reservationOffers);
  const items = reservationOffers.map((offer, idx) => {
    const taxDetails = calculatePriceAndTax(offer.vehicleOfferPrice, offer.tax);
    return {
      ...mapVehicleOffer(offer),
      index: idx,
      price: taxDetails.priceBeforeTax,
    };
  });
  const customParams = {
    item_list_name: "Vehicles",
    start_date: startTime,
    end_date: returnTime,
    pick_up_location: company.displayName,
    place_of_return: company.displayName,
    booking_length_in_days: daysBetweenDates(startTime, returnTime),
  };
  const params = {
    items: items,
    ...customParams,
  };
  // console.log("gtmViewItemList", params);
  gtagPageView("view_item_list", params);
}

export function enableGTMEvents(company) {
  if (company.features?.enableGTM === "ECOMMERCE") {
    return true;
  }
  return false;
}

function gtagPageView(eventName, params) {
  // console.log("gtag", eventName, params);
  if (window?.gtag) window.gtag("event", eventName, params);
  else if (window?.dataLayer) {
    window.dataLayer?.push({ event: eventName, ecommerce: params });
  }
}
